// ============================================================================
// ResetPasswordPage — route /account/reset?token= (PUBLIC, trainer realm).
// ----------------------------------------------------------------------------
// The trainer follows the emailed reset link, lands here with ?token=, and sets
// a new password (POST /api/auth/reset via useAuth().reset). The server
// validates the token (single-use / unexpired / subject-bound → 400 otherwise),
// writes the new argon2 hash to that trainer's row only, consumes the token, and
// signs the trainer straight in (shared vf_session cookie) — so on success we
// drop them into their studio. New password must be >= 10 chars (trainer rule),
// validated client-side BEFORE the request so a too-short attempt never burns
// the token.
//
// Mobile-first Pulse, reduced-motion-aware, EN + 繁中 via i18n keys only.
// ============================================================================
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Banner, Button, Card, TextField } from "../components";
import { ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { LanguageToggle } from "../i18n/LanguageToggle";

const EASE = [0.22, 0.61, 0.36, 1] as const;
const MIN_PASSWORD = 10; // trainer rule (matches the backend reset endpoint).

function BrandMark() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="inline-flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-grad-coral shadow-volt">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <path
            d="M3 13h4l2-6 3 11 2.6-8L18 13h3"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="font-display text-lg font-extrabold tracking-tightest text-body">
        {t("app.name", { ns: "common" })}
      </span>
    </Link>
  );
}

export function ResetPasswordPage() {
  const { t, ready } = useTranslation("forgot");
  const reduce = useReducedMotion();
  const { reset, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // A trainer who is already signed in has no business on the reset screen.
  if (!isLoading && isAuthenticated && !submitting)
    return <Navigate to="/app" replace />;

  if (!ready) return <div className="min-h-screen bg-paper" aria-hidden />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError(t("reset.noToken"));
      return;
    }
    // Validate length client-side BEFORE the request so a too-short attempt
    // never consumes the single-use token.
    if (password.length < MIN_PASSWORD) {
      setError(t("reset.tooShortTrainer"));
      return;
    }
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await reset({ token, new_password: password });
      // The server signed us in; drop straight into the studio.
      navigate("/app", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("reset.invalidToken"));
      } else if (err instanceof ApiError && err.status === 422) {
        setError(t("reset.tooShortTrainer"));
      } else {
        setError(t("reset.genericError"));
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-body">
      <header className="mx-auto flex max-w-md items-center justify-between px-5 py-5">
        <BrandMark />
        <LanguageToggle />
      </header>
      <main className="mx-auto flex max-w-md flex-col px-5 pb-16 pt-2">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
        >
          <Card className="space-y-5">
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
                {t("reset.kicker")}
              </p>
              <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
                {t("reset.title")}
              </h1>
              <p className="mt-1.5 text-body-soft">{t("reset.blurb")}</p>
            </div>

            {!token && <Banner tone="warning" title={t("reset.noToken")} />}
            {error && <Banner tone="danger" title={error} />}

            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <div className="relative">
                <TextField
                  label={t("reset.newPassword")}
                  type={show ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  hint={t("reset.hintTrainer")}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  required
                  disabled={submitting || !token}
                />
              </div>
              <TextField
                label={t("reset.confirm")}
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setError(null);
                }}
                required
                disabled={submitting || !token}
              />
              <label className="flex items-center gap-2 text-sm text-body-soft">
                <input
                  type="checkbox"
                  checked={show}
                  onChange={(e) => setShow(e.target.checked)}
                  className="h-4 w-4 rounded border-line text-coral focus-visible:ring-volt"
                />
                {t("reset.showPassword")}
              </label>
              <Button
                type="submit"
                block
                size="lg"
                disabled={submitting || !token}
              >
                {submitting ? t("reset.saving") : t("reset.submit")}
              </Button>
            </form>

            <p className="text-center text-sm text-body-soft">
              {t("remembered")}{" "}
              <Link
                to="/login"
                className="font-bold text-coral transition-colors hover:text-volt-600"
              >
                {t("backToLogin")}
              </Link>
            </p>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
